import * as React from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import TopBar from '../../components/TopBar';
import SideBar from '../../components/SideBar';
import { colors } from '../../assets/utils/colors';
import HotToastfy from '../../components/HotToastfy';

// importing slices
import { setBranches } from '../../features/branchSlice';
import { setProducts } from '../../features/productSlice';
import { setPromotions } from '../../features/promotionSlice';
import { setOrders } from '../../features/orderSlice';
import { setBrands } from '../../features/brandSlice';

// RTK query api imports
import {
    useGetBranchesQuery,
    useGetProductsQuery,
    useGetPromotionsQuery,
    useGetOrdersQuery,
    useGetBrandsQuery,
} from '../../api/dashboard/dashboardAPI';
import { useDispatch, useSelector } from 'react-redux';
import FullAppLoader from '../../components/FullAppLoader';
import { getRTKResponseData } from '../../helpers/RTKHelpers';
import { branchesSelector } from '../../features/selectors';
import {
    useGetAllSubCategoriesQuery,
    useGetCategoriesQuery,
} from '../../api/categories/categoryAPI';
import { setCategories, setSubCategories } from '../../features/categorySlice';

const DrawerHeader = styled(Box)(({ theme }) => ({
    ...theme.mixins.toolbar,
}));

//! ################## MAIN FUNC ##########################
const AppLayout = ({ children }) => {
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const [desktopOpen, setDesktopOpen] = React.useState(true);
    const handleDrawerOpen = () => {
        setDesktopOpen(true);
    };
    const handleDrawerClose = () => {
        setDesktopOpen(false);
    };

    // ################# CONST ##################
    const dispatch = useDispatch();

    // ################# Redux state ################
    const { refetchBranches } = useSelector(branchesSelector);

    // ################# RTK ####################
    const {
        isLoading: branchesLoading,
        isSuccess: branchSuccess,
        data: branches,
        refetch: branchesRefetch,
    } = useGetBranchesQuery();
    const {
        isLoading: productsLoading,
        isSuccess: productSuccess,
        data: products,
    } = useGetProductsQuery();
    const {
        isLoading: ordersLoading,
        isSuccess: orderSuccess,
        data: orders,
    } = useGetOrdersQuery();
    const {
        isLoading: PromotionLoading,
        isSuccess: promotionSuccess,
        data: promotions,
    } = useGetPromotionsQuery();
    const {
        isLoading: brandsLoading,
        isSuccess: brandSuccess,
        data: brands,
    } = useGetBrandsQuery();
    const {
        isLoading: categoriesLoading,
        isSuccess: categoriesuccess,
        data: categories,
    } = useGetCategoriesQuery();
    const {
        isLoading: subCategoriesLoading,
        isSuccess: subCategoriesuccess,
        data: subCategories,
    } = useGetAllSubCategoriesQuery();

    // ################ MAIN dispatch ####################
    React.useEffect(() => {
        let isSubscribed = true;
        if (isSubscribed) {
            if (branchSuccess) {
                dispatch(setBranches(getRTKResponseData(branches)));
            }
            if (productSuccess) {
                dispatch(setProducts(getRTKResponseData(products)));
            }
            if (orderSuccess) {
                dispatch(setOrders(getRTKResponseData(orders)));
            }
            if (promotionSuccess) {
                dispatch(setPromotions(getRTKResponseData(promotions)));
            }
            if (brandSuccess) {
                dispatch(setBrands(getRTKResponseData(brands)));
            }
            if (categoriesuccess) {
                dispatch(setCategories(getRTKResponseData(categories)));
            }
            if (subCategoriesuccess) {
                dispatch(setSubCategories(getRTKResponseData(subCategories)));
            }
        }

        return () => {
            isSubscribed = false;
        };
    }, [
        branchSuccess,
        productSuccess,
        orderSuccess,
        promotionSuccess,
        brandSuccess,
        branches,
        brands,
        dispatch,
        orders,
        products,
        promotions,
        categoriesLoading,
        categoriesuccess,
        categories,
        subCategoriesLoading,
        subCategoriesuccess,
        subCategories,
    ]);

    // ################ REFETCH #################
    React.useEffect(() => {
        let isSubscribed = true;
        if (isSubscribed) {
            branchesRefetch();
        }

        return () => {
            isSubscribed = false;
        };
    }, [refetchBranches, branchesRefetch]);

    return (
        <>
            {branchesLoading ||
            productsLoading ||
            ordersLoading ||
            PromotionLoading ||
            categoriesLoading ||
            subCategoriesLoading ||
            brandsLoading ? (
                <>
                    <FullAppLoader />
                </>
            ) : (
                <Box
                    sx={{
                        display: 'flex',
                        position: 'absolute',
                        top: 0,
                        bottom: '100%',
                        right: 0,
                        left: 0,
                    }}
                >
                    {/* ############## Tostfy ############### */}
                    <HotToastfy />

                    {/* ############### topbar ############### */}
                    <TopBar
                        mobileOpen={mobileOpen}
                        desktopOpen={desktopOpen}
                        handleDrawerClose={handleDrawerClose}
                        handleDrawerToggle={handleDrawerToggle}
                    />

                    {/* ############### sidebar ###############*/}
                    <SideBar
                        mobileOpen={mobileOpen}
                        desktopOpen={desktopOpen}
                        handleDrawerOpen={handleDrawerOpen}
                        handleDrawerClose={handleDrawerClose}
                    />

                    {/* ############### content ###############*/}
                    <Box
                        component="main"
                        sx={{ bgcolor: colors.secondary, flexGrow: 1 }}
                    >
                        <DrawerHeader />
                        {children}
                    </Box>
                </Box>
            )}
        </>
    );
};

export default AppLayout;
